import { maxPowerWaterAuthorityReason } from './maxPowerWaterAuthority';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
/**
 * MAX-POWER → PROJECT INTENT stamp, and its staleness contract.
 *
 * Stamping writes `powerRequirementMW` in the SAME transaction as the
 * provenance fields. That is deliberate: every downstream consumer
 * (`effectivePower`, LCOE, scoring, every report template) keeps working
 * unchanged, while `powerSizingMode` + `fieldSource.powerRequirementMW` record
 * that the number was DERIVED rather than declared. It mirrors how promotion
 * already writes `phases[].powerSourceConfig` instead of making every reader
 * learn a new resolution rule.
 *
 * ═══ STALENESS IS THE WHOLE POINT OF THE HASHES ═══
 *
 * A max-power figure is only true for one scenario, at one layout, on one
 * parcel. Change the rotation and the land ceiling moves; change the fleet and
 * every ceiling moves. `maxPowerStudyStalenessReason` returns the FIRST reason
 * the stamp no longer describes reality, so the UI can say WHICH input drifted
 * rather than a generic "out of date".
 */

import {
  GENERATION_MAX_POWER_STUDY_MODEL_VERSION,
  GENERATION_MAX_POWER_STUDY_SCHEMA_VERSION,
} from '../../types/generationImpact';
import type {
  GenerationImpactLayoutHead,
  GenerationImpactScenarioSnapshot,
  GenerationMaxPowerStudySnapshot,
} from '../../types/generationImpact';
import type { ProjectIntent } from '../../types/siteRag';
import { bucketFromMw, withPhaseCapacityMax } from '../projectIntent';
import { hashGenerationImpactPayload } from './integrity';

/** Provenance token written to `fieldSource.powerRequirementMW`. */
export const MAX_POWER_FIELD_SOURCE = 'site-max-power-study' as const;

/** Stable hash of a study body, used to detect a swapped/edited snapshot. */
export function hashMaxPowerStudy(study: GenerationMaxPowerStudySnapshot): string {
  return hashGenerationImpactPayload(study);
}

export interface BuildMaxPowerIntentStampArgs {
  intent: ProjectIntent;
  scenario: GenerationImpactScenarioSnapshot;
  study: GenerationMaxPowerStudySnapshot;
  sed?: SiteEvidenceDocument | null;
  layoutHead?: GenerationImpactLayoutHead | null;
  now: string;
  uid?: string;
}

/**
 * Project the stamp onto an intent. PURE — returns a new intent, never mutates.
 * Throws when the study carries no maximum, because stamping "no answer" as a
 * capacity would be exactly the fabrication this feature exists to avoid.
 *
 * On a PHASED intent the stamp also writes the ULTIMATE phase's cumulative
 * `capacityMW` (with `capacitySource` provenance) — see the inline note below
 * for why a top-level-only stamp is invisible on phased sites.
 */
export function buildMaxPowerIntentStamp(args: BuildMaxPowerIntentStampArgs): ProjectIntent {
  const { intent, scenario, study, layoutHead, now, uid } = args;
  if (study.status !== 'available' || !study.maximum) {
    throw new RangeError('A max-power study without a resolved maximum cannot be stamped onto project intent.');
  }
  if (!study.boundQuality) {
    throw new RangeError('A max-power study without a bound quality cannot be stamped: the figure would be unqualified.');
  }

  const waterReason = maxPowerWaterAuthorityReason(scenario, study, args.sed);
  if (waterReason) throw new RangeError(waterReason);

  const facilityMW = study.maximum.facilityMW;
  const fieldSource = {
    ...(intent.fieldSource ?? {}),
    // Provenance key on ProjectIntent.fieldSource — the CANONICAL home the SSOT
    // sentinel itself names. The field it retired is the per-report-type
    // Preferences copy, not this one.
    powerRequirementMW: MAX_POWER_FIELD_SOURCE, // SSOT-SENTINEL-OK
  } as ProjectIntent['fieldSource'];

  // PHASED sites: the stamp must ALSO land on the ULTIMATE (max-sequence)
  // phase, because `resolveIntentPowerStack` reads the phases' cumulative
  // ultimate — a top-level-only stamp is invisible the moment phases exist.
  // Under the cumulative convention the study's facility MW IS the final
  // phase's total-at-completion, so the two figures agree byte-for-byte and
  // the reconcile-on-save (`reconcilePowerRequirementWithPhases`) sees a
  // coherent intent → no rewrite → the provenance below survives the next
  // save. Replacement is IN PLACE by id, preserving array order — `phases`
  // is inside `hashProjectIntent` (stableStringify keeps array order), so a
  // re-sort here would churn the intent hash and re-dirty the SED for free.
  // `withPhaseCapacityMax` drops any `capacityBucket` and marks
  // `capacitySource: 'site-max-power-study'` on the phase.
  const phases = intent.phases ?? [];
  let stampedPhases: ProjectIntent['phases'];
  if (phases.length > 0) {
    const ultimateId = phases
      .slice()
      .sort((a, b) => a.sequence - b.sequence)[phases.length - 1].id;
    stampedPhases = phases.map(p => (p.id === ultimateId ? withPhaseCapacityMax(p, facilityMW) : p));
  }

  return {
    ...intent,
    ...(stampedPhases ? { phases: stampedPhases } : {}),
    // The FACILITY figure is the campus size, and the intent's default basis is
    // 'facility' — so the derived MW lands on the layer it actually describes.
    powerRequirementMW: facilityMW, // SSOT-SENTINEL-OK — canonical projectIntent field
    powerRequirement: bucketFromMw(facilityMW),
    powerSizingMode: 'site-max-power',
    fieldSource,
    maxPowerStudy: {
      scenarioId: scenario.id,
      scenarioInputHash: scenario.inputHash,
      scenarioResultHash: scenario.resultHash,
      studySchemaVersion: GENERATION_MAX_POWER_STUDY_SCHEMA_VERSION,
      studyModelVersion: GENERATION_MAX_POWER_STUDY_MODEL_VERSION,
      studyHash: hashMaxPowerStudy(study),
      ...(layoutHead?.publicationId ? { layoutPublicationId: layoutHead.publicationId } : {}),
      ...(typeof layoutHead?.version === 'number' ? { layoutVersion: layoutHead.version } : {}),
      ...(layoutHead?.layoutHash ? { layoutHash: layoutHead.layoutHash } : {}),
      ...(study.basis.parcelId ? { parcelId: study.basis.parcelId } : {}),
      firmNetMW: study.maximum.firmNetMW,
      facilityMW,
      itLoadMW: study.maximum.itLoadMW,
      bindingConstraintIds: [...study.bindingConstraintIds],
      boundQuality: study.boundQuality,
      stampedAt: now,
      ...(uid ? { stampedBy: uid } : {}),
    },
  };
}

export interface MaxPowerStalenessInputs {
  intent: ProjectIntent | null | undefined;
  /** The scenario currently promoted for this site. */
  promotedScenario: GenerationImpactScenarioSnapshot | null | undefined;
  layoutHead?: GenerationImpactLayoutHead | null;
  /** The stored study body, for the tamper check. */
  storedStudy?: GenerationMaxPowerStudySnapshot | null;
  sed?: SiteEvidenceDocument | null;
}

/**
 * The FIRST reason a stamped max-power figure no longer describes reality, or
 * null when it is fresh. Order matters — the earliest reason is the most
 * actionable one to show a user.
 */
export function maxPowerStudyStalenessReason(args: MaxPowerStalenessInputs): string | null {
  const stamp = args.intent?.maxPowerStudy;
  if (!stamp) return null; // nothing stamped ⇒ nothing to be stale

  // 1. Model / schema moved under the stamp.
  if (stamp.studyModelVersion !== GENERATION_MAX_POWER_STUDY_MODEL_VERSION
    || stamp.studySchemaVersion !== GENERATION_MAX_POWER_STUDY_SCHEMA_VERSION) {
    return 'The capacity model changed since this figure was stamped; re-run the max-power study.';
  }

  // 2. A different scenario is promoted now.
  const promoted = args.promotedScenario;
  if (!promoted) {
    return 'The scenario this figure came from is no longer promoted; re-run the max-power study.';
  }
  if (promoted.id !== stamp.scenarioId) {
    return 'This figure was stamped from a different generation scenario than the one now promoted.';
  }

  // 3. The same scenario changed underneath it.
  if (promoted.inputHash !== stamp.scenarioInputHash) {
    return 'The promoted scenario\'s inputs changed since this figure was stamped.';
  }
  if (promoted.resultHash !== stamp.scenarioResultHash) {
    return 'The promoted scenario\'s results changed since this figure was stamped.';
  }

  const waterReason = maxPowerWaterAuthorityReason(promoted, args.storedStudy, args.sed);
  if (waterReason) return waterReason;

  // 4. The layout moved — the LAND ceiling is layout-conditional, so this is a
  //    real invalidation, not a cosmetic one.
  if (stamp.layoutHash && args.layoutHead?.layoutHash && stamp.layoutHash !== args.layoutHead.layoutHash) {
    return 'The published layout changed; the land ceiling is layout-conditional, so this figure no longer holds.';
  }
  if (typeof stamp.layoutVersion === 'number'
    && typeof args.layoutHead?.version === 'number'
    && stamp.layoutVersion !== args.layoutHead.version) {
    return 'A newer layout version was published since this figure was stamped.';
  }

  // 5. The stored body does not match its own stamp (edited or swapped).
  if (args.storedStudy && hashMaxPowerStudy(args.storedStudy) !== stamp.studyHash) {
    return 'The stored max-power study does not match its stamp; re-run the study.';
  }

  return null;
}

/**
 * Whether the intent's MW figure is currently DERIVED. Readers that render the
 * number should surface the provenance rather than presenting it as declared.
 */
export function isMaxPowerDerivedIntent(intent: ProjectIntent | null | undefined): boolean {
  return intent?.powerSizingMode === 'site-max-power' && intent.maxPowerStudy != null;
}
